import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { getStates } from "@/lib/api";
import { T } from "@/context/LanguageContext";
import { Seo } from "@/components/site/Seo";
import { itemListJsonLd } from "@/lib/seo";
import { SectionHeading } from "@/components/home/ExploreByState";

const REGIONS = ["All", "Northeast", "Southeast", "Midwest", "Southwest", "West", "Pacific", "Territory"];

// Legacy USAStates.com 9-region grouping (order preserved from the archive).
const HISTORICAL_ORDER = [
  "new-england", "mid-atlantic", "appalachian-highlands", "the-southeast",
  "great-lakes", "the-heartland", "the-southwest", "mountain-states",
  "pacific-coast", "distant-shores",
];

export default function ExploreStates() {
  const [all, setAll] = useState([]);
  const [region, setRegion] = useState("All");
  const [mode, setMode] = useState("census"); // census | historical
  const [params] = useSearchParams();
  const typeFilter = params.get("type");

  useEffect(() => {
    if (params.get("group") === "historical") setMode("historical");
  }, [params]);

  useEffect(() => { getStates().then((d) => setAll(d.states)).catch(() => {}); }, []);

  const list = useMemo(() => {
    let l = all;
    if (typeFilter === "territory") l = l.filter((s) => s.geo_type === "territory");
    if (mode === "census" && region !== "All") l = l.filter((s) => s.region === region);
    return l;
  }, [all, region, typeFilter, mode]);

  // Group by the legacy historical region for the "historical" view.
  const historicalGroups = useMemo(() => {
    const by = {};
    list.forEach((s) => {
      const k = s.historical_region || "other";
      (by[k] = by[k] || []).push(s);
    });
    const ordered = HISTORICAL_ORDER.filter((k) => by[k]).map((k) => ({
      key: k, label: by[k][0].historical_region_label || k, states: by[k],
    }));
    if (by.other) ordered.push({ key: "other", label: "Other", states: by.other });
    return ordered;
  }, [list]);

  return (
    <div className="mx-auto max-w-[1500px] px-4 py-12">
      <Seo
        title={typeFilter === "territory" ? "Explore U.S. Territories" : "Explore All 50 States"}
        path={typeFilter === "territory" ? "/explore/states?type=territory" : "/explore/states"}
        description={typeFilter === "territory"
          ? "Browse every U.S. territory and island area on USAPulse — Puerto Rico, Guam, U.S. Virgin Islands and more, each with its own knowledge portal."
          : "Explore all 50 U.S. states plus the federal district and territories on USAPulse — each with its people, history, culture, economy, counties and cities."}
        jsonLd={itemListJsonLd(
          typeFilter === "territory" ? "U.S. Territories" : "U.S. States",
          list.map((s) => ({ name: s.official_name, url_path: s.url_path }))
        )}
      />
      <SectionHeading
        kicker={typeFilter === "territory" ? "U.S. Territories & Island Areas" : "50 States · 1 Federal District · 5 Territories"}
        title={typeFilter === "territory" ? "Explore U.S. Territories" : "Explore the United States"}
      />

      <div className="mb-6 flex flex-wrap items-center gap-2">
        <span className="mr-1 text-[11px] font-bold uppercase tracking-widest text-muted-foreground"><T>Group by</T>:</span>
        <button
          onClick={() => setMode("census")}
          className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${mode === "census" ? "bg-oldglory-red text-white" : "bg-parchment text-navy hover:bg-navy/10"}`}
          data-testid="mode-census"
        ><T>Census Regions</T></button>
        <button
          onClick={() => setMode("historical")}
          className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${mode === "historical" ? "bg-oldglory-red text-white" : "bg-parchment text-navy hover:bg-navy/10"}`}
          data-testid="mode-historical"
        ><T>Historical Regions</T></button>
      </div>

      {mode === "census" && (
        <div className="mb-8 flex flex-wrap gap-2">
          {REGIONS.map((r) => (
            <button
              key={r}
              onClick={() => setRegion(r)}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                region === r ? "bg-navy text-white" : "bg-parchment text-navy hover:bg-navy/10"
              }`}
              data-testid={`region-filter-${r.toLowerCase()}`}
            >
              <T>{r}</T>
            </button>
          ))}
        </div>
      )}

      {mode === "historical" ? (
        <div className="space-y-12" data-testid="historical-regions">
          <p className="max-w-3xl text-sm text-muted-foreground">
            <T>The nine historical/geographic groupings from a classic 2012 state portal — a richer regional map than the Census divisions.</T>
          </p>
          {historicalGroups.map((g) => (
            <section key={g.key} data-testid={`historical-group-${g.key}`}>
              <div className="mb-4 flex items-center gap-3">
                <Link to={`/region/${g.key}`} className="group inline-flex items-center gap-2" data-testid={`region-link-${g.key}`}>
                  <h2 className="font-display text-xl font-black tracking-tight text-navy group-hover:text-oldglory-red sm:text-2xl">{g.label}</h2>
                  <span className="text-oldglory-red opacity-0 transition-opacity group-hover:opacity-100">→</span>
                </Link>
                <span className="rounded-full bg-navy/5 px-2.5 py-0.5 text-xs font-semibold text-navy">{g.states.length}</span>
                <Link to={`/region/${g.key}`} className="ml-auto text-xs font-semibold text-oldglory-red hover:underline" data-testid={`region-hub-link-${g.key}`}><T>Region hub</T> →</Link>
              </div>
              <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
                {g.states.map((s) => <StateCard key={s.geo_id} s={s} />)}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
          {list.map((s, i) => <StateCard key={s.geo_id} s={s} i={i} />)}
        </div>
      )}
    </div>
  );
}

function StateCard({ s, i = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: (i % 8) * 0.04 }}
    >
      <Link to={`/place${s.url_path}`} className="group block overflow-hidden rounded-xl bg-white shadow-md ring-1 ring-border transition-all hover:shadow-2xl hover:ring-navy/30" data-testid={`explore-card-${s.geo_id}`}>
        <div className="relative h-40 overflow-hidden">
          <img src={s.image_url} alt={s.official_name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy/70 to-transparent" />
          <div className="absolute bottom-3 left-3 right-3">
            <div className="font-display text-lg font-bold text-white">{s.official_name}</div>
            <div className="text-xs text-white/80">{s.nickname}</div>
          </div>
          <span className="absolute right-2 top-2 rounded bg-white/90 px-1.5 py-0.5 text-[10px] font-bold text-navy uppercase">{s.geo_type === "territory" ? "TERR" : s.geo_type === "federal_district" ? "FED" : s.state_code}</span>
        </div>
        <div className="flex items-center justify-between px-4 py-2.5 text-xs text-muted-foreground">
          <span>{s.capital}</span>
          <span>{s.population?.toLocaleString()}</span>
        </div>
      </Link>
    </motion.div>
  );
}
